/**
 * Editorial content for /pricing.
 *
 * Prices and plan features are read from `constants/config`, which is what the
 * app itself bills against, rather than restated here. The page previously kept
 * its own copy and had drifted: it advertised five credit packs at prices that
 * no longer existed, and quoted a per-credit rate for a pack that had been
 * retired. Anything a customer could hold us to is derived, not typed.
 */
import {
  DEFAULT_MODEL_FREE_API_NOTE,
  FREE_USER_STARTING_CREDITS_DISPLAY,
  PLUS_MONTHLY_CREDITS,
  PLUS_MONTHLY_CREDITS_DISPLAY,
  PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY,
  PRICING_PLANS,
} from './config';
import { MODEL_TOTAL, PROVIDER_TOTAL } from './modelInventory.generated';

export { CREDIT_EXAMPLES } from './modelInventory.generated';

const { plus, plusPremium } = PRICING_PLANS;

/** Monthly cost on each billing cadence, and what annual actually saves. */
export const BILLING = {
  monthly: plus.monthlyPrice,
  annualPerMonth: plus.annualMonthlyPrice,
  annualTotal: plus.annualPrice,
  savingsPercent: Math.round(
    (1 - Number(plus.annualMonthlyPrice) / Number(plus.monthlyPrice)) * 100,
  ),
};

export const PREMIUM_BILLING = {
  monthly: plusPremium.monthlyPrice,
  annualPerMonth: plusPremium.annualMonthlyPrice,
  annualTotal: plusPremium.annualPrice,
  savingsPercent: Math.round(
    (1 - Number(plusPremium.annualMonthlyPrice) / Number(plusPremium.monthlyPrice)) * 100,
  ),
};

export const HERO_TRUST = [
  'Free to try',
  'No credit card to start',
  'Cancel anytime',
];

/**
 * The plans. `highlight` marks the one the page argues for (Plus); Premium is
 * positioned for businesses & power users and is never the default CTA.
 */
export const PLANS = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'Everything to try, nothing to lose.',
    price: '$0',
    cadence: 'to try',
    note: `${FREE_USER_STARTING_CREDITS_DISPLAY} credits on signup`,
    cta: 'Start free',
    href: '/signup',
    features: PRICING_PLANS.free.features,
  },
  {
    id: 'plus',
    name: 'Plus',
    tagline: 'Your AI agent for real work.',
    cadence: 'per month',
    note: 'Billed annually, or month to month',
    cta: 'Get Plus',
    href: '/signup',
    highlight: true,
    features: plus.features,
  },
  {
    id: 'plus-premium',
    name: 'Plus Premium',
    tagline: 'For businesses and power users.',
    cadence: 'per month',
    note: 'Web only · billed annually, or month to month',
    cta: 'Get Plus Premium',
    href: '/signup',
    badge: plusPremium.badge,
    features: plusPremium.features,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'Built with you, deployed where you need it.',
    price: 'Custom',
    cadence: 'talk to us',
    note: 'Private cloud or on-premise',
    cta: 'Contact sales',
    href: '/enterprise',
    features: [
      { name: 'Custom AI solutions built by our team', included: true },
      { name: 'Dedicated AI engineering team', included: true },
      { name: 'Custom model fine-tuning and training', included: true },
      { name: 'Enterprise-grade security and compliance', included: true },
      { name: 'On-premise or private cloud deployment', included: true },
      { name: 'Ongoing support and optimization', included: true },
    ],
  },
];

/** What a Plus subscription actually opens up, stated as countable things. */
export const PLUS_FIGURES = [
  { value: 'Work', label: 'Agentic mode included' },
  { value: String(MODEL_TOTAL), label: 'Live models included' },
  { value: String(PROVIDER_TOTAL), label: 'Providers, one bill' },
  { value: PLUS_MONTHLY_CREDITS_DISPLAY, label: 'Credits every month' },
];

export const CREDIT_PILLARS = [
  {
    icon: 'infinity',
    title: 'The default model is free',
    copy: `Chatting with our default model in the app costs nothing for Plus subscribers — no per-message credit, no daily cap. ${DEFAULT_MODEL_FREE_API_NOTE}.`,
  },
  {
    icon: 'coins',
    title: `${PLUS_MONTHLY_CREDITS_DISPLAY} credits a month`,
    copy: 'Credits are what you spend on everything else: frontier chat models, image and video generation, music, voice, and 3D. They refresh at the start of each billing cycle.',
  },
  {
    icon: 'cart-plus',
    title: 'Top up whenever',
    copy: 'Run low mid-month and you can buy a pack outright. Purchased credits never expire — they sit in your account until you spend them.',
  },
];

/**
 * The live packs, mirroring `backend/config.js`. Kept to the fields a visitor
 * needs; the per-credit rate is computed at render so it cannot contradict the
 * price beside it.
 */
export const CREDIT_PACKS = [
  { credits: 700, price: 10 },
  { credits: 2200, price: 25, badge: 'Most popular' },
  { credits: 5000, price: 50, badge: 'Best value' },
];

/** Cheapest per-credit rate on offer, used by the packs lede and the FAQ. */
export const BEST_PACK = CREDIT_PACKS.reduce((best, pack) => (
  pack.price / pack.credits < best.price / best.credits ? pack : best
));

export const FAQ = [
  {
    question: 'What is Work mode, and which plan includes it?',
    answer: 'Work mode is the agentic side of Gab AI: it plans multi-step jobs, researches, works with files and tools, and keeps going until it delivers a finished result. Work mode is included with Plus and Plus Premium; it is not available on the Free plan. Plus includes up to 3 active Work sessions at once; Plus Premium includes up to 10. Wait for a session to finish or stop one to free a slot.',
  },
  {
    question: 'What is a credit, and what does it buy?',
    answer: `Credits are how you pay for the models that cost us money to run — frontier chat models, image and video generation, music, voice, and 3D. Each model has its own price, from a few credits for a message or an image up to a few hundred for a textured 3D asset. Plus includes ${PLUS_MONTHLY_CREDITS_DISPLAY} credits a month; Plus Premium includes ${PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY}.`,
  },
  {
    question: 'What is Plus Premium?',
    answer: `Plus Premium is a web-only plan for businesses and power users. It includes everything in Plus, plus ${PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY} monthly credits, up to 10 Work sessions at once (compared with 3 on Plus), higher API rate limits, and priority email support. Most people are happiest on Plus — Premium is there when you need more headroom.`,
  },
  {
    question: 'Does chatting cost credits?',
    answer: `Not with our default model, if you're on Plus and chatting in the app — that's unlimited and free. Other models draw from your credit balance. ${DEFAULT_MODEL_FREE_API_NOTE}: every API request draws credits, including with the default model.`,
  },
  {
    question: 'What happens when I run out?',
    answer: 'You keep unlimited access to the default chat model, so you are never locked out of Gab AI. Work mode and other credit-using models pause until your monthly credits refresh or you buy a credit pack.',
  },
  {
    question: 'Do unused credits roll over?',
    answer: 'Monthly subscription credits reset at the start of each billing cycle. Credits from purchased packs behave differently — they never expire and stay in your account until you use them.',
  },
  {
    question: 'Can I change or cancel my plan?',
    answer: 'Any time, from your account settings. There is no cancellation fee and no phone call. If you cancel, you keep your subscription until the end of the period you have already paid for.',
  },
  {
    question: 'Can I export my data if I leave?',
    answer: 'Yes. You can download a complete archive of your account — chats, memories, files, agents, and collections — from settings or through the API. Your data is yours, and it comes with you.',
  },
  {
    question: 'Are credit packs refundable?',
    answer: 'Credit pack purchases are final. Because purchased credits never expire, nothing you buy goes to waste — you can spend it whenever you get to it.',
  },
  {
    question: 'What payment methods do you take?',
    answer: 'Credit cards issued by US banks, everywhere. Outside the App Store we also take Bitcoin, which is the route most international customers use for both subscriptions and credit packs.',
  },
];

/** Kept in one place so the closing band and the hero make the same promise. */
export const CLOSING_TRUST = [
  `${FREE_USER_STARTING_CREDITS_DISPLAY} credits free`,
  'No credit card',
  'Cancel anytime',
];

export const PLUS_MONTHLY_CREDIT_COUNT = PLUS_MONTHLY_CREDITS;
